/**
 * Geode Voice (UPT-2000 V1a, spec web V1 §5.2) — ligne de consommation de la voix :
 * « Cette session : 02:10 · Ce mois : 22 % du plafond (13 sur 60 min) · ≈ 0,84 CHF ».
 *
 * Rendue dans `VoicePanel` et `VoiceDrawerSection`, SEULEMENT pendant ou après une session réelle de la page
 * (jamais en simulateur). Texte simple (pas de Markdown) ; durées, pourcentages et montants isolés en bidi
 * (`NumericValue` : `<bdi dir="ltr">` + chiffres tabulaires) pour rester lisibles en arabe.
 *
 * Couleurs d'ÉTAT (jamais la marque du locataire) : < 80 % info, >= 80 % avertissement, >= 100 % danger.
 * Un seul `GET …/voice/usage` à l'ouverture (jamais de sondage) ; l'arrêt de session en lance un second.
 */
import { useEffect } from 'react';
import { useIntl } from '@/react-intl';
import { NumericValue } from '@/uptk/core/components/ui/NumericValue';
import { useVoiceStore } from './voiceStore';
import { refreshVoiceUsage } from './voiceUsageRefresh';
import type { VoiceUsage } from './voiceTypes';

export type VoiceUsageTone = 'info' | 'warning' | 'danger';

/** Seuils du plafond : < 80 % info, >= 80 % avertissement, >= 100 % danger. */
export const toneForPercent = (percent: number): VoiceUsageTone => {
  if (percent >= 100) {
    return 'danger';
  }
  return percent >= 80 ? 'warning' : 'info';
};

const TEXT_CLASSES: Record<VoiceUsageTone, string> = {
  info: 'text-info-text',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
};

const FILL_CLASSES: Record<VoiceUsageTone, string> = {
  info: 'bg-info-solid',
  warning: 'bg-warning-solid',
  danger: 'bg-danger-solid',
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** `mm:ss` (la durée maximale d'une session tient en minutes : 60 min au plus côté Azure). */
export const formatClock = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds));
  return `${pad(Math.floor(safe / 60))}:${pad(safe % 60)}`;
};

/** Montant en devise (`Intl.NumberFormat`) ; une devise inconnue retombe sur « montant code ». */
export const formatCost = (locale: string, amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    const plain = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${plain.format(amount)} ${currency}`;
  }
};

const formatDay = (locale: string, iso: string): string | null => {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(time));
  } catch {
    return null;
  }
};

type CostView = { kind: 'amount' | 'partial'; amount: number; currency: string } | { kind: 'unknown' } | null;

/**
 * `cost == null` (pas de droit de facturation) → rien. Aucun tarif côté serveur (montant nul et
 * `complete = false`) → « coût non estimé ». Tarifs partiels → montant suivi de « (tarifs partiels) ».
 */
const costView = (usage: VoiceUsage | null): CostView => {
  if (!usage || !usage.cost) {
    return null;
  }
  const { amount, currency, complete } = usage.cost;
  if (!complete && amount <= 0) {
    return { kind: 'unknown' };
  }
  return { kind: complete ? 'amount' : 'partial', amount, currency };
};

export type VoiceUsageLineProps = {
  /** Classes de la rangée (marges propres au conteneur : panneau ou tiroir). */
  className?: string;
};

export function VoiceUsageLine({ className = '' }: Readonly<VoiceUsageLineProps>) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  // `intl.locale` est typé large par le shim `@/react-intl` : valeur sûre pour `Intl.*`.
  const locale = typeof intl.locale === 'string' && intl.locale ? intl.locale : 'fr';
  const realSession = useVoiceStore((store) => store.realSession);
  const mockActive = useVoiceStore((store) => store.mockActive);
  const sessionSeconds = useVoiceStore((store) => store.sessionSeconds);
  const limits = useVoiceStore((store) => store.limits);
  const sessionSource = useVoiceStore((store) => store.source);
  const monthlySecondsUsed = useVoiceStore((store) => store.monthlySecondsUsed);
  const usage = useVoiceStore((store) => store.usage);
  const usageStatus = useVoiceStore((store) => store.usageStatus);

  const visible = realSession && !mockActive;

  // Un seul GET à l'ouverture de la ligne ; les ouvertures rapprochées ne le refont pas (USAGE_FRESH_MS).
  useEffect(() => {
    if (visible) {
      void refreshVoiceUsage();
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  const source = sessionSource ?? usage?.source ?? null;
  const used = monthlySecondsUsed ?? 0;
  const max = usage?.seconds.max ?? limits?.monthlySecondsMax ?? null;
  const percent = max && max > 0 ? Math.round((used / max) * 100) : null;
  const tone = toneForPercent(percent ?? 0);
  const cost = costView(usage);
  const resetDay =
    source === 'geode' && percent !== null && percent >= 80 && usage?.resetAt
      ? formatDay(locale, usage.resetAt)
      : null;

  const usedMinutes = Math.floor(used / 60);

  let monthPart = null;
  if (source === 'tenant') {
    // Clé du locataire : aucun plafond, donc aucune barre ni pourcentage.
    monthPart = (
      <span className="text-portal-ink-muted">
        {intl.formatMessage(
          { id: 'voice.usage.monthOwnKey' },
          { used: <NumericValue key="used">{usedMinutes}</NumericValue> }
        )}
      </span>
    );
  } else if (percent !== null && max) {
    monthPart = (
      <span className={TEXT_CLASSES[tone]}>
        {intl.formatMessage(
          { id: 'voice.usage.month' },
          {
            percent: <NumericValue key="percent">{percent}</NumericValue>,
            used: <NumericValue key="used">{usedMinutes}</NumericValue>,
            max: <NumericValue key="max">{Math.round(max / 60)}</NumericValue>,
          }
        )}
      </span>
    );
  }

  let costPart = null;
  if (cost?.kind === 'unknown') {
    costPart = <span className="text-portal-ink-muted">{t('voice.usage.costUnknown')}</span>;
  } else if (cost) {
    const amount = <NumericValue key="amount">{formatCost(locale, cost.amount, cost.currency)}</NumericValue>;
    costPart = (
      <span className="text-portal-ink-muted">
        {cost.kind === 'partial'
          ? intl.formatMessage({ id: 'voice.usage.costPartial' }, { amount })
          : intl.formatMessage({ id: 'voice.usage.cost' }, { amount })}
      </span>
    );
  }

  return (
    <div
      role="group"
      aria-label={t('voice.usage.title')}
      className={`flex min-w-0 flex-col gap-1 text-xs ${className}`}
    >
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-portal-ink">
          {intl.formatMessage(
            { id: 'voice.usage.session' },
            { duration: <NumericValue key="duration">{formatClock(sessionSeconds)}</NumericValue> }
          )}
        </span>
        {monthPart}
        {costPart}
      </p>
      {source === 'geode' && percent !== null ? (
        <div
          role="progressbar"
          aria-label={t('voice.usage.title')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(100, percent)}
          className="h-1.5 w-full overflow-hidden rounded-full bg-portal-surface-muted"
        >
          <div
            className={`h-full rounded-full ${FILL_CLASSES[tone]}`}
            style={{ inlineSize: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        </div>
      ) : null}
      {resetDay ? (
        <p className="text-portal-ink-muted">
          {intl.formatMessage(
            { id: 'voice.usage.reset' },
            { date: <NumericValue key="date">{resetDay}</NumericValue> }
          )}
        </p>
      ) : null}
      {usageStatus === 'error' ? (
        <p className="text-portal-ink-muted">{t('voice.usage.unavailable')}</p>
      ) : null}
    </div>
  );
}

export default VoiceUsageLine;
